import type { Artifact, ArtifactType, Field, Task } from '../../store/types';
import type { ArtifactState, FieldState, TaskState } from './state';

const toTaskState = (task: Task): TaskState => ({
  kind: 'task',
  id: task.id,
  name: task.name,
  markdown: task.prompt,
  fields: task.fields.map(field => ({
    uid: field.id,
    key: field.name,
    markdown: field.prompt,
    typeLabel: field.type,
    enumValues: field.enumValues ?? null,
    criteria: field.criteria ?? null,
    children: field.children ?? null,
  })),
  artifacts: task.artifacts.map(artifact => ({
    uid: artifact.id,
    name: artifact.name,
    isStarred: artifact.isStarred,
    mimeType: artifact.type,
    markdown: artifact.spec,
  })),
});

// `unit` has no spike counterpart, so rows merge onto the record they came from rather than being
// rebuilt — reconstructing would silently drop it on the first edit.
const mergeField = (previous: Field | undefined, next: FieldState): Field => ({
  ...previous,
  id: next.uid,
  name: next.key,
  prompt: next.markdown,
  type: (next.typeLabel || previous?.type || 'text') as Field['type'],
  enumValues: next.enumValues ?? undefined,
  criteria: next.criteria ?? undefined,
  children: next.children ?? undefined,
});

const mergeArtifact = (previous: Artifact | undefined, next: ArtifactState): Artifact => ({
  ...previous,
  id: next.uid,
  name: next.name,
  isStarred: next.isStarred,
  spec: next.markdown,
  type: (next.mimeType || previous?.type || 'markdown') as ArtifactType,
});

const fromTaskState = (task: Task, next: TaskState): Partial<Task> => {
  const fieldsById = new Map(task.fields.map(field => [field.id, field]));
  const artifactsById = new Map(task.artifacts.map(artifact => [artifact.id, artifact]));

  return {
    name: next.name,
    prompt: next.markdown,
    fields: next.fields.map(field => mergeField(fieldsById.get(field.uid), field)),
    artifacts: next.artifacts.map(artifact => mergeArtifact(artifactsById.get(artifact.uid), artifact)),
  };
};

export { fromTaskState, toTaskState };
